// src/components/User/UserDashboard.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import UrlConfig from '../../data/UrlConfig';
import { getApi } from '../../services/apiService';
import { useGlow } from '../../hooks/useGlow';
import { newsData } from '../../data/newsData';
import { getStaticContentConfig } from '../../data/staticContentConfig';

import {
  ArrowRightIcon,
  GiftIcon,
  BoltIcon,
  SparklesIcon,
  CubeIcon,
  MegaphoneIcon,
  XMarkIcon,
  StarIcon,
} from '@heroicons/react/24/solid';

// Diccionario de textos
const translations = {
  en: {
    dashboardTitle: (name) => `${name}'s Dashboard`,
    oracleDeck: "Oracle Deck",
    latestNews: "Latest News",
    premiumCollection: "Premium Collection",
    yourGifts: "Your Gifts",
    noProducts: "You currently don't have any purchased products.",
    exploreProducts: "Explore Our Products",
    viewContent: "View Content",
    noDescription: "No description available.",
    redirecting: "Redirecting...",
    readings: [
      { title: "Your Daily Card", subtitle: "Get guidance for your path", path: "/oracle/reading/today" },
      { title: "State of Being", subtitle: "Get guidance for the issue on your mind", path: "/oracle/reading/state" },
      { title: "Triple Energy Reading", subtitle: "Get guidance for the issue on your mind today", path: "/oracle/reading/triple" },
      { title: "Five Star Reading", subtitle: "Get a complete reading for a specific topic", path: "/oracle/reading/star" },
    ]
  },
  es: {
    dashboardTitle: (name) => `Panel de ${name}`,
    oracleDeck: "Baraja del Oráculo",
    latestNews: "Últimas Noticias",
    premiumCollection: "Colección Premium",
    yourGifts: "Tus Regalos",
    noProducts: "Actualmente no tienes productos comprados.",
    exploreProducts: "Explorar Productos",
    viewContent: "Ver Contenido",
    noDescription: "No hay descripción disponible.",
    redirecting: "Redirigiendo...",
    readings: [
      { title: "Tu Carta Diaria", subtitle: "Obtén guía para tu camino", path: "/oraculo/lectura/hoy" },
      { title: "Lectura de Cinco Estrellas", subtitle: "Una lectura completa para un tema específico", path: "/oraculo/lectura/estrella" },
    ]
  }
};

// Helper para imagen random dinámico por idioma
const getRandomCardImage = (lang) => 
  `https://gusaguiar.com/assets/oracle/deck-en/${Math.floor(Math.random() * 89) + 1}.png`;

// --- COMPONENTE DE CARTAS APILADAS (CardStack) ---
const CardStack = ({ count, lang }) => {
  const cards = useMemo(() => {
    return [...Array(count)].map(() => getRandomCardImage(lang));
  }, [count, lang]);

  return (
    <div className="relative h-20 w-16 flex items-center justify-center">
      {cards.map((imgSrc, i) => {
        const offset = i - Math.floor(count / 2);
        const rotation = offset * 10; 
        const translateX = offset * 8; 
        return (
          <img
            key={i}
            src={imgSrc}
            alt={`Card ${i}`}
            className="absolute h-full w-auto object-contain rounded-md shadow-xl border border-gray-600/60 bg-gray-900"
            style={{
              zIndex: i, 
              transformOrigin: '50% 90%',
              transform: `translateX(${translateX}px) rotate(${rotation}deg)`,
            }}
          />
        );
      })}
    </div>
  );
};

const ProductIcon = ({ type, imageUrl }) => {
  const iconClasses = "w-8 h-8 flex-shrink-0";
  
  if (imageUrl) {
    return <img src={imageUrl} alt="Product" className="w-12 h-12 object-cover rounded-md flex-shrink-0" />;
  }

  switch (type) {
    case 'premium':
      return <StarIcon className={`${iconClasses} text-yellow-400`} />;
    case 'gift':
      return <GiftIcon className={`${iconClasses} text-yellow-500`} />;
    case 'air':
      return <BoltIcon className={`${iconClasses} text-cyan-400`} />;
    case 'ohm':
      return <SparklesIcon className={`${iconClasses} text-purple-400`} />;
    default:
      return <CubeIcon className={`${iconClasses} text-gray-400`} />;
  }
};

// --- COMPONENTE PARA RENDERIZAR CADA PRODUCTO ---
const ProductCard = ({ product, t, prefix }) => (
  <Link to={`${prefix}/user${product.pathUrl}`} className="block bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 p-6 relative group transform hover:-translate-y-1">
    <div className="flex items-start gap-4 mb-4">
      <ProductIcon type={product.type} imageUrl={product.imageUrl} />
      <div>
        <h2 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-200">{product.title}</h2>
        <span className="text-xs font-mono text-gray-500 uppercase">{product.category}</span>
      </div>
    </div>
    <p className="text-gray-300 text-sm mb-6 line-clamp-3">{product.description || t.noDescription}</p>
    <div className="flex items-center justify-end text-yellow-400 font-semibold group-hover:text-yellow-300 transition-colors duration-200">
      <span className="mr-2">{t.viewContent}</span>
      <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
    </div>
  </Link>
);

const DashboardTitledSeparator = ({ title, style }) => (
  <div className="relative my-12">
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-700" />
    </div>
    <div className="relative flex justify-center">
      <span className="bg-gray-900 px-6">
        <h2 style={style} className="text-2xl md:text-3xl font-bold text-yellow-400">{title}</h2>
      </span>
    </div>
  </div>
);

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Detectar idioma desde la URL: si contiene '/es/' o empieza por '/es', usa 'es', sino 'en'
  const lang = user?.lang == 'es' ? 'es' : 'en';
  const prefix = `/` + lang;

  const staticContent = useMemo(() => getStaticContentConfig(lang), [lang]);
  
  const t = translations[lang] || translations['en'];

  const [userProducts, setUserProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dismissedNews, setDismissedNews] = useState(() => {
    const saved = localStorage.getItem('dismissedNews');
    return saved ? JSON.parse(saved) : [];
  });

  // Categorización de productos
  const categorizedProducts = useMemo(() => {
    const premium = userProducts.filter(p => p.type === 'premium');
    const gifts = userProducts.filter(p => p.type === 'gift');
    return { premium, gifts };
  }, [userProducts]);

  const latestNews = newsData.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3);
  const visibleNews = latestNews.filter(item => !dismissedNews.includes(item.title));

  const handleDismissNews = (newsTitle, e) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedDismissed = [...dismissedNews, newsTitle];
    setDismissedNews(updatedDismissed);
    localStorage.setItem('dismissedNews', JSON.stringify(updatedDismissed));
  };

  let separatorStyle = useGlow({
    gradient: 'linear-gradient(to bottom, #e0e0e0ff, #a3a190ff, #7c6f4dff, #aa9e7fff)',
    intensityMin: 0.2,
    intensityMax: 0.4,
    intervalDuration: 3000,
  });

  useEffect(() => {
    if (!user) { 
      navigate(`${prefix}/login?redirect=${encodeURIComponent(location.pathname)}`); 
      return; 
    }
    const fetchUserProducts = async () => {
      setIsLoading(true);
      try {
        const data = await getApi(UrlConfig.user_get_products);
        setUserProducts(data);
      } catch (err) { setError(err.message); } finally { setIsLoading(false); }
    };
    fetchUserProducts();
  }, [user, navigate, prefix, location.pathname]);

  if (!user) return <div className="flex justify-center items-center min-h-screen bg-gray-900 text-white">{t.redirecting}</div>;

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-extrabold text-yellow-400 mb-10 mt-10 text-center" style={{ fontFamily: '"Cinzel Decorative", serif' }}>
          {t?.dashboardTitle(user?.username)}
        </h1>

        <DashboardTitledSeparator title={t.oracleDeck} style={separatorStyle} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-3xl mx-auto">
          {t.readings.map((item, idx) => (
            <Link 
              key={idx} 
              to={`${item.path}`} 
              className="bg-gray-800 p-6 rounded-xl shadow-xl hover:shadow-2xl transition-all flex items-center gap-6 group transform hover:-translate-y-1"
            >
              <CardStack count={3} lang={lang} />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white group-hover:text-yellow-400">{item.title}</h2>
                <p className="text-gray-300 text-sm mt-1">{item.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Noticias */}
        {!isLoading && !error && visibleNews.length > 0 && (
          <>
            <DashboardTitledSeparator title={t.latestNews} style={separatorStyle} />
            <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden max-w-3xl mx-auto mb-12">
              {visibleNews.map((newsItem) => (
                <div key={newsItem.title} className="group relative flex items-center justify-between border-b border-gray-700 last:border-b-0 hover:bg-gray-700/50">
                  <Link to={`${newsItem.link}`} className="flex flex-grow items-center gap-4 p-4">
                    <MegaphoneIcon className="w-5 h-5 text-green-400" />
                    <span className="text-base font-medium text-gray-200">{newsItem.title}</span>
                  </Link>
                  <button onClick={(e) => handleDismissNews(newsItem.title, e)} className="p-4 text-gray-500 hover:text-white">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Productos */}
        {!isLoading && userProducts.length > 0 ? (
          <>
            {/* Sección Premium */}
            {categorizedProducts.premium.length > 0 && (
              <>
                <DashboardTitledSeparator title={t.premiumCollection} style={separatorStyle} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                  {categorizedProducts.premium.map(p => <ProductCard key={p.code} product={p} t={t} prefix={prefix} />)}
                </div>
              </>
            )}
            {/* Sección Gifts */}
            {categorizedProducts.gifts.length > 0 && (
              <>
                <DashboardTitledSeparator title={t.yourGifts} style={separatorStyle} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                  {categorizedProducts.gifts.map(p => <ProductCard key={p.code} product={p} t={t} prefix={prefix} />)}
                </div>
              </>
            )}
          </>
        ) : !isLoading && !error && (
            <div className="bg-gray-800 p-10 rounded-lg shadow-lg text-center mx-auto max-w-lg mb-12">
              <p className="text-2xl mb-6 text-gray-300">{t.noProducts}</p>
              <Link to={`${prefix}/shop`} className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-medium rounded-md hover:bg-yellow-500">
                {t.exploreProducts}
              </Link>
            </div>
        )}

        {/* Contenido Estático */}
        {!isLoading && !error && staticContent.map(({ category, items }) => (
          <div key={category}>
            <DashboardTitledSeparator title={category} style={separatorStyle} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map(({ key, path, Icon, color, title, description }) => (
                <Link key={key} to={`${prefix}${path}`} className="block bg-gray-800 rounded-xl shadow-xl hover:shadow-2xl p-6 group transform hover:-translate-y-1">
                  <div className="flex items-start gap-4 mb-4">
                    <Icon className={`w-8 h-8 ${color}`} />
                    <h2 className="text-2xl font-bold text-white group-hover:text-yellow-400">{title}</h2>
                  </div>
                  <p className="text-gray-300 mb-6">{description}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserDashboard;